import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

import { useAuthStore } from '@/stores/auth.store';

/**
 * Axios instance compartilhada.
 *
 * - `baseURL`: `/api` — o Vite proxy do dev mapeia `/api/*` →
 *   `localhost:3000/*` (sem prefixo). O prefixo evita colisão com rotas
 *   do React Router (`/onboarding`, `/login`, `/dashboard`). Em build de
 *   produção, o frontend serve na mesma origem da API atrás de um reverse
 *   proxy que também aceita `/api` (decisão pós-MVP).
 * - `withCredentials: true` — necessário para o browser enviar os cookies
 *   httpOnly de auth (P1-Auth / ADR 0018).
 * - Response interceptor (Sprint 05 — fecha R-022; ajustado em P1-Auth):
 *   em 401 numa rota privada (não-skip), tenta refresh automático via
 *   `POST /auth/refresh` com body vazio (o cookie `refresh_token` viaja
 *   automaticamente). Cache de Promise burst-safe + flag `_retried` para
 *   evitar loop. Skip URLs explícitas (D15): rotas de auth e onboarding
 *   não disparam refresh.
 *
 * Decisões consolidadas em ADR 0007 e ADR 0018.
 */

/** Marca opcional na config para o próprio interceptor pular ele. */
interface ConfigEstendida extends InternalAxiosRequestConfig {
  _retried?: boolean;
  _skipAuthRefresh?: boolean;
}

const SKIP_REFRESH_PATHS = ['/auth/login', '/auth/refresh', '/auth/logout', '/onboarding'];

/**
 * Promise em-curso do `POST /auth/refresh`. Burst de 5 requests 401 dispara
 * 1 só refresh — os demais aguardam a Promise existente. `null` quando não
 * há refresh em andamento.
 */
let refreshInFlight: Promise<void> | null = null;

export const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
  timeout: 30_000,
});

// P1-Auth: o request interceptor que injetava `Authorization: Bearer` foi
// removido — autenticação agora é via cookie httpOnly (gerido pelo browser).

api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const original = error.config as ConfigEstendida | undefined;

    if (
      !original ||
      error.response?.status !== 401 ||
      original._retried ||
      original._skipAuthRefresh ||
      ehSkipUrl(original.url)
    ) {
      return Promise.reject(error);
    }

    // Se não há `usuario` no store, nunca esteve logado — não tenta refresh.
    if (!useAuthStore.getState().usuario) {
      return Promise.reject(error);
    }

    original._retried = true;

    try {
      await iniciarOuAguardarRefresh();
      // Reemite o request original — cookie novo já está no browser.
      return api(original);
    } catch (refreshErr) {
      // Refresh falhou (401 expirado, reuso detectado, etc) → encerra sessão.
      useAuthStore.getState().logout();
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      return Promise.reject(refreshErr);
    }
  },
);

function ehSkipUrl(url: string | undefined): boolean {
  if (!url) return false;
  // O `url` pode ser absoluto (`http://...`) ou relativo (`/auth/login` ou
  // `auth/login` quando vem do `baseURL`). Normaliza pra match prefixo.
  for (const skip of SKIP_REFRESH_PATHS) {
    if (url.includes(skip)) return true;
  }
  return false;
}

/**
 * Se já existe refresh em curso, retorna a Promise existente (cache). Senão,
 * dispara um novo `POST /auth/refresh` com body vazio (o cookie viaja
 * automaticamente) e cacheia até resolver/rejeitar. `finally` limpa o cache
 * (próximos 401 podem rodar refresh de novo).
 */
function iniciarOuAguardarRefresh(): Promise<void> {
  if (refreshInFlight) {
    return refreshInFlight;
  }
  // `_skipAuthRefresh` impede que o próprio call de refresh, se receber 401,
  // dispare outro refresh recursivo.
  const config: AxiosRequestConfig & { _skipAuthRefresh: boolean } = {
    _skipAuthRefresh: true,
  };
  refreshInFlight = api
    .post('/auth/refresh', {}, config)
    .then(() => undefined)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

/**
 * Extrai mensagem de erro amigável de uma resposta de erro Axios.
 * Backend retorna `{ message: string | string[] }` (NestJS ValidationPipe
 * pode mandar array). Helper achata para uma string única.
 */
export function mensagemDeErro(err: unknown, fallback = 'Erro inesperado.'): string {
  if (!err) return fallback;
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: string | string[] } | undefined;
    if (Array.isArray(data?.message)) return data.message.join(' ');
    if (typeof data?.message === 'string') return data.message;
    if (err.message) return err.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

/** Exposto p/ testes — limpa `refreshInFlight` entre `it()`s. */
export function _resetRefreshInFlightParaTeste(): void {
  refreshInFlight = null;
}
